import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "@/App.css";
import { Header } from "@/components/landing/Header";
import { Hero } from "@/components/landing/Hero";
import { TrustStrip } from "@/components/landing/TrustStrip";
import { Audience } from "@/components/landing/Audience";
import { Recruitment } from "@/components/landing/Recruitment";
import { Industries } from "@/components/landing/Industries";
import { GlobalMobility } from "@/components/landing/GlobalMobility";
import { StudyAbroad } from "@/components/landing/StudyAbroad";
import { Scholarships } from "@/components/landing/Scholarships";
import { WhyAfrinexa } from "@/components/landing/WhyAfrinexa";
import { AfrinexaAI } from "@/components/landing/AfrinexaAI";
import { Trust } from "@/components/landing/Trust";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { Contact } from "@/components/landing/Contact";
import { Footer } from "@/components/landing/Footer";
import { FloatingWhatsApp } from "@/components/landing/FloatingWhatsApp";
import RecruitmentPage from "@/pages/RecruitmentPage";
import JobsPage from "@/pages/JobsPage";
import JobDetailPage from "@/pages/JobDetailPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminForgotPassword from "@/pages/AdminForgotPassword";
import AdminResetPassword from "@/pages/AdminResetPassword";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminInbox from "@/pages/AdminInbox";
import AdminJobs from "@/pages/AdminJobs";
import AdminApplications from "@/pages/AdminApplications";
import AdminCandidates from "@/pages/AdminCandidates";
import AdminEmployers from "@/pages/AdminEmployers";
import AdminJobReview from "@/pages/AdminJobReview";
import AdminJobAlerts from "@/pages/AdminJobAlerts";
import AdminUsers from "@/pages/AdminUsers";
import AdminSettings from "@/pages/AdminSettings";
import CandidateLogin, {
    CandidateForgotPassword,
    CandidateRegister,
    CandidateResetPassword,
} from "@/pages/candidate/CandidateAuth";
import CandidateDashboard from "@/pages/candidate/Dashboard";
import CandidateProfile from "@/pages/candidate/Profile";
import CandidateApplications from "@/pages/candidate/Applications";
import CandidateApplicationDetail from "@/pages/candidate/ApplicationDetail";
import SavedJobs from "@/pages/candidate/SavedJobs";
import CandidateAlerts from "@/pages/candidate/Alerts";
import CandidateSettings from "@/pages/candidate/Settings";
import EmployerLogin, { EmployerRegister } from "@/pages/employer/EmployerAuth";
import EmployerDashboard from "@/pages/employer/Dashboard";
import EmployerProfile from "@/pages/employer/Profile";
import EmployerVerification from "@/pages/employer/Verification";
import EmployerJobForm from "@/pages/employer/JobForm";
import EmployerJobs from "@/pages/employer/Jobs";
import EmployerApplicants from "@/pages/employer/Applicants";
import EmployerInterviews from "@/pages/employer/Interviews";
import EmployerRequests from "@/pages/employer/Requests";
import EmployerMessages from "@/pages/employer/Messages";
import EmployerDocuments from "@/pages/employer/Documents";
import EmployerNotifications from "@/pages/employer/Notifications";
import EmployerSettings from "@/pages/employer/Settings";
import AgentOSOverview from "@/pages/AgentOSOverview";
import AgentOSAgents from "@/pages/AgentOSAgents";
import AgentOSTasks from "@/pages/AgentOSTasks";
import AgentOSMissions from "@/pages/AgentOSMissions";
import AgentOSTools from "@/pages/AgentOSTools";
import AgentOSApprovals from "@/pages/AgentOSApprovals";
import AgentOSMemory from "@/pages/AgentOSMemory";
import AgentOSUsage from "@/pages/AgentOSUsage";
import AgentOSAudit from "@/pages/AgentOSAudit";
import AgentOSModels from "@/pages/AgentOSModels";
import AgentOSRouting from "@/pages/AgentOSRouting";
import AgentOSPrompts from "@/pages/AgentOSPrompts";
import AgentOSScorecards from "@/pages/AgentOSScorecards";
import AgentOSEvaluations from "@/pages/AgentOSEvaluations";
import AgentOSKnowledge from "@/pages/AgentOSKnowledge";
import AgentOSProviderAdapter from "@/pages/AgentOSProviderAdapter";
import AgentOSModelReview from "@/pages/AgentOSModelReview";
import AgentOSVectorStore from "@/pages/AgentOSVectorStore";
import AgentOSCanaries from "@/pages/AgentOSCanaries";
import AgentOSPilot from "@/pages/AgentOSPilot";

const ScrollManager = () => {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const el = document.querySelector(hash);
            if (el) {
                el.scrollIntoView({ behavior: "smooth" });
                return;
            }
        }
        window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
};

const HomePage = () => (
    <>
        <Hero />
        <TrustStrip />
        <Recruitment />
        <Audience />
        <Industries />
        <GlobalMobility />
        <StudyAbroad />
        <Scholarships />
        <WhyAfrinexa />
        <AfrinexaAI />
        <Trust />
        <FinalCTA />
        <Contact />
    </>
);

const Shell = () => {
    const { pathname } = useLocation();
    const isSystem = pathname.startsWith("/admin") || pathname.startsWith("/account") || pathname.startsWith("/employer");
    return (
        <div className="min-h-screen bg-ivory font-sans text-ink antialiased">
            <ScrollManager />
            {!isSystem && <Header />}
            <main>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/recruitment" element={<RecruitmentPage />} />
                    <Route path="/jobs" element={<JobsPage />} />
                    <Route path="/jobs/:slug" element={<JobDetailPage />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                    <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route
                        path="/admin/recruitment-requests"
                        element={<AdminInbox fixedType="recruitment" activeKey="recruitment" pageTitle="Recruitment Requests" pageSubtitle="Review employer recruitment requests, update their status and download attached requirement documents." />}
                    />
                    <Route
                        path="/admin/contact-enquiries"
                        element={<AdminInbox fixedType="contact" activeKey="contact" pageTitle="Contact Enquiries" pageSubtitle="Review website enquiries, update their status and reply using the contact details provided." />}
                    />
                    <Route path="/admin/submissions" element={<AdminInbox activeKey="dashboard" pageTitle="Submissions" pageSubtitle="All recruitment requests and contact enquiries." />} />
                    <Route path="/admin/jobs" element={<AdminJobs />} />
                    <Route path="/admin/applications" element={<AdminApplications />} />
                    <Route path="/admin/candidates" element={<AdminCandidates />} />
                    <Route path="/admin/employers" element={<AdminEmployers />} />
                    <Route path="/admin/review" element={<AdminJobReview />} />
                    <Route path="/admin/alerts" element={<AdminJobAlerts />} />
                    <Route path="/admin/users" element={<AdminUsers />} />
                    <Route path="/admin/settings" element={<AdminSettings />} />
                    <Route path="/admin/agent-os" element={<AgentOSOverview />} />
                    <Route path="/admin/agent-os/agents" element={<AgentOSAgents />} />
                    <Route path="/admin/agent-os/tasks" element={<AgentOSTasks />} />
                    <Route path="/admin/agent-os/missions" element={<AgentOSMissions />} />
                    <Route path="/admin/agent-os/models" element={<AgentOSModels />} />
                    <Route path="/admin/agent-os/model-review" element={<AgentOSModelReview />} />
                    <Route path="/admin/agent-os/canaries" element={<AgentOSCanaries />} />
                    <Route path="/admin/agent-os/pilot" element={<AgentOSPilot />} />
                    <Route path="/admin/agent-os/vector-store" element={<AgentOSVectorStore />} />
                    <Route path="/admin/agent-os/routing" element={<AgentOSRouting />} />
                    <Route path="/admin/agent-os/prompts" element={<AgentOSPrompts />} />
                    <Route path="/admin/agent-os/scorecards" element={<AgentOSScorecards />} />
                    <Route path="/admin/agent-os/evaluations" element={<AgentOSEvaluations />} />
                    <Route path="/admin/agent-os/knowledge" element={<AgentOSKnowledge />} />
                    <Route path="/admin/agent-os/provider" element={<AgentOSProviderAdapter />} />
                    <Route path="/admin/agent-os/tools" element={<AgentOSTools />} />
                    <Route path="/admin/agent-os/approvals" element={<AgentOSApprovals />} />
                    <Route path="/admin/agent-os/memory" element={<AgentOSMemory />} />
                    <Route path="/admin/agent-os/usage" element={<AgentOSUsage />} />
                    <Route path="/admin/agent-os/audit" element={<AgentOSAudit />} />
                    <Route path="/account/login" element={<CandidateLogin />} />
                    <Route path="/account/register" element={<CandidateRegister />} />
                    <Route path="/account/forgot-password" element={<CandidateForgotPassword />} />
                    <Route path="/account/reset-password" element={<CandidateResetPassword />} />
                    <Route path="/account" element={<CandidateDashboard />} />
                    <Route path="/account/profile" element={<CandidateProfile />} />
                    <Route path="/account/applications" element={<CandidateApplications />} />
                    <Route path="/account/applications/:id" element={<CandidateApplicationDetail />} />
                    <Route path="/account/saved" element={<SavedJobs />} />
                    <Route path="/account/alerts" element={<CandidateAlerts />} />
                    <Route path="/account/settings" element={<CandidateSettings />} />
                    <Route path="/employer/login" element={<EmployerLogin />} />
                    <Route path="/employer/register" element={<EmployerRegister />} />
                    <Route path="/employer" element={<EmployerDashboard />} />
                    <Route path="/employer/profile" element={<EmployerProfile />} />
                    <Route path="/employer/verification" element={<EmployerVerification />} />
                    <Route path="/employer/jobs/new" element={<EmployerJobForm />} />
                    <Route path="/employer/jobs" element={<EmployerJobs />} />
                    <Route path="/employer/applicants" element={<EmployerApplicants />} />
                    <Route path="/employer/shortlisted" element={<EmployerApplicants fixedStatus="shortlisted" />} />
                    <Route path="/employer/interviews" element={<EmployerInterviews />} />
                    <Route path="/employer/requests" element={<EmployerRequests />} />
                    <Route path="/employer/messages" element={<EmployerMessages />} />
                    <Route path="/employer/documents" element={<EmployerDocuments />} />
                    <Route path="/employer/notifications" element={<EmployerNotifications />} />
                    <Route path="/employer/settings" element={<EmployerSettings />} />
                </Routes>
            </main>
            {!isSystem && <Footer />}
            {!isSystem && <FloatingWhatsApp />}
        </div>
    );
};

export default function App() {
    return (
        <BrowserRouter>
            <Shell />
        </BrowserRouter>
    );
}
